import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Field } from "./Form";

export interface SelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  emptyText?: string;
}

/**
 * A searchable single-select combobox. Self-contained (absolutely-positioned
 * dropdown + outside-click close) so it works correctly inside a modal without
 * portal/focus-trap conflicts.
 */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  label,
  error,
  required,
  placeholder = "Select…",
  emptyText = "No matches",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const choose = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <Field label={label} error={error} required={required}>
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="input flex w-full items-center justify-between text-left"
        >
          <span className={selected ? "truncate" : "truncate text-slate-500"}>
            {selected?.label ?? placeholder}
          </span>
          <ChevronsUpDown size={16} className="ml-2 shrink-0 text-slate-400" />
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-slate-700 bg-slate-800 shadow-xl">
            <div className="flex items-center gap-2 border-b border-slate-700 px-3 py-2">
              <Search size={15} className="shrink-0 text-slate-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setOpen(false);
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (filtered[0]) choose(filtered[0].value);
                  }
                }}
                placeholder="Search…"
                className="w-full bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
              />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-slate-500">{emptyText}</div>
              ) : (
                filtered.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => choose(o.value)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-700 ${
                      o.value === value ? "text-blue-400" : "text-slate-200"
                    }`}
                  >
                    <span className="truncate">{o.label}</span>
                    {o.value === value && <Check size={15} className="shrink-0" />}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </Field>
  );
};
